import { Directive, DOCUMENT, ElementRef, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Popover } from '@now/dpl';

/**
 * Initialisiert Bootstrap-Tooltips
 */
@Directive({
  /*
    "The selector should be prefixed by "nowWbsucheLib" (https://angular.io/guide/styleguide#style-02-08)"
    wir wollen hier wissentlich das native HTML-Element beeinflussen und verwenden deswegen KEINEN prefix
  */
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[data-bs-toggle="popover"]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly document = inject<Document>(DOCUMENT);

  private mutationObserver!: MutationObserver;
  private popover?: Popover;

  ngOnInit(): void {
    this.initTooltip();

    this.mutationObserver = new this.document.defaultView!.MutationObserver(
      this.triggerTooltipUpdate
    );
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      attributes: true,
      childList: false,
      characterData: false
    });
  }

  private readonly triggerTooltipUpdate: MutationCallback = (entries) =>
    entries.forEach((entry) => {
      if (entry.attributeName == 'data-bs-content') {
        this.removeTooltip();
        this.initTooltip();
      }
    });

  private removeTooltip(): void {
    try {
      this.popover?.dispose();
    } catch (ex) {
      // Irrelevant, falls beim Löschen der Tooltips Fehler auftreten.
    }
  }

  private initTooltip() {
    // Änderungen am DOM durch das JS der DPL sollen die ChangeDetection von Angular nicht auslösen
    const heading = this.elementRef.nativeElement.hasAttribute('data-heading')
      ? this.elementRef.nativeElement.dataset.heading
      : 'h6';
    const options: Partial<Popover.Options> = {
      trigger: 'hover',
      html: true,
      delay: {
        show: 0,
        hide: 200
      },
      container: 'body',
      fallbackPlacements: ['top', 'bottom'],
      offset: [0, 10],
      template: `<div class="popover" role="tooltip"><div class="popover-arrow"></div><${heading} class="popover-header"></${heading}><div class="popover-body"></div></div>`
    };

    this.popover = this.document.defaultView!.dpl?.bs?.Popover.getOrCreateInstance(
      this.elementRef.nativeElement,
      options
    );
  }

  /**
   * Schließt den Tooltip, auch wenn auf das Element mit dem Tooltip geklickt wird und dadurch
   * das auslösende Element aus dem DOM entfernt wird z. B. weil eine Seitennavigation durch
   * dadurch ausgelöst wird.
   */
  ngOnDestroy(): void {
    this.popover?.hide();
    this.popover?.dispose();
    this.mutationObserver?.disconnect();
  }
}
